import React, { useEffect, useState } from 'react'
import { TMyServiceProps } from './MyServices.types'
import styles from './MyServices.module.scss'
import {
  Button,
  ButtonType,
  Card,
  CardContainer,
  CardHeader,
  CardSizeTypes,
  Icon,
  StandardModal,
  Tag,
  TagType,
  Tooltip
} from '@digicert/dcone-common-ui'
import { faExternalLinkSquare, faTimes } from '@digicert/dcone-common-ui/src/components/icons/light'
import { faEllipsisV } from '@digicert/dcone-common-ui/src/components/icons/regular'
import { connect } from 'react-redux'
import { Translate } from 'react-localize-redux'
import { IAccessScope, IApplication } from '../../reducers/Reducers.interfaces'
import { fetchUserProfile } from 'core/actions/Profile.actions'
import { renderManagerIcon, translate } from '../../shared/helpers/utils'
import { IAppState } from 'core/store/store'
import { getServices } from 'secure-pages/settings/Settings.actions'
import { Link } from 'react-router-dom'
import { getItemFromLS, setItemToLS } from '../../shared/helpers/LocalStorage'
import { RouterConfig } from '../../core/utils/constants'
import { ReactComponent as CertCentralName } from '../../assets/images/dc-certcentral-name.svg'
import { Dropdown } from 'antd'
import { isEqual } from 'lodash-es'
import { STMApplicationName } from 'core/constants'

const MyServices = (
    {
      fetchUserProfile,
      getServices,
      userProfile
    }: TMyServiceProps) => {
  const [applications, setApplications] = useState<Array<IApplication> | undefined>()
  const [otherApplications, setOtherApplications] = useState<Array<IApplication> | undefined>()
  const [cardToolTip, setCardToolTip] = useState(true)
  const CERT_CENTRAL_TIP_DISMISSED = 'cert-central-tip-dismissed'
  const [showServicesModal, setShowServicesModal] = useState(false)
  const [disableCardClick, setDisableCardClick] = useState(false)
  const [startRefreshTimer, setStartRefreshTimer] = useState(false)
  const [refreshTime, setRefreshTime] = useState(new Date())

  const dismissToolTip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCardToolTip(false)
    setItemToLS(CERT_CENTRAL_TIP_DISMISSED, 'true')
  }

  useEffect(() => {
    if (userProfile === undefined) {
      fetchUserProfile(true)
    }
    getServices().then(res => {
      setApplications(res[0].my_services)
      setOtherApplications(res[0].other_services)
    })
  }, []);

  useEffect(() => {
    if(userProfile?.access_scope === IAccessScope.system) {
      window.location.href = RouterConfig.accounts.table
    }
  }, []);

  const handleActionClick = (e, item: IApplication) => {
    if (item.service_provider_accounts && item.service_provider_accounts.length > 1) {
      setShowServicesModal(true)
    }
    else {
      window.open(item.url, '_blank');
      e.preventDefault();
    }
  };

  const handleAccountCardClick = (url: string) => {
    window.open(url, '_blank');
  }

  const cardContent = (item : IApplication) => {
    if (item.service_provider_accounts && item.service_provider_accounts.length > 0) {
      return (
          <div className={styles.accountTags}>
            <Tag type={TagType.INFO}>{item.service_provider_accounts.length + ' ' + translate('common.accounts') as string}</Tag>
          </div>
      )
    }
  }

  const renderCard = (item: IApplication) => {
    return (<Card
      size={CardSizeTypes.LARGE}
      onClick={!disableCardClick ? (e) => { handleActionClick(e, item) } : undefined}
      header={
        <div className={styles.customCardHeader}>
          <div className={styles.iconContainer}>{renderManagerIcon(item.icon, item.name)}</div>
          <p>{item.icon === 'stIcon' ? item.name + " | " + STMApplicationName.KEYLOCKER : item.name}</p>
          {item.new_account_linking_url !== undefined && item.multiple_service_account_linking &&
            <Dropdown trigger={['click']} menu={{ items: cardHeaderOverFlowItems(item.new_account_linking_url) }} onOpenChange={(open) => {
              setDisableCardClick(open)
            }}>
              <button
                aria-label={translate('common.form.buttons.moreActions') as string}
                className={styles.overflowMenuButton}
                onClick={(event) => event.stopPropagation()}
              >
                <Icon icon={faEllipsisV} />
              </button>
            </Dropdown>
          }
        </div>
      }
      content=
      {
        (
          <>
            <div className={styles.customCardContent}>
              <p><Translate id={'applications.' + item.app_code.split('.')[0] + '.shortDescription'} /></p>
              {cardContent(item)}
            </div>
          </>
        )
      }
    >
    </Card>)
  }

  const cardHeaderOverFlowItems = (url: string) =>  [
    {
      label: <Link to={{ pathname: url}} target='_blank' onClick={() => setStartRefreshTimer(true)}>{translate('common.form.buttons.addAccount') as string}</Link>,
      key: 'overflow-item-add-account',
    }
  ];

  // if applications and on adding account and startRefreshTimer update list of services after a minute
  useEffect(() => {
    if(applications && applications.length > 0 && startRefreshTimer) {
      let currentServices: Array<IApplication> = [...applications]

      const intervalId = setInterval(() => {
        setRefreshTime(new Date())
        getServices().then(res => {
          if(isEqual(currentServices, res[0].my_services)) {
            setStartRefreshTimer(true)
          }
          else {
            setApplications(res[0].my_services)
            setStartRefreshTimer(false)
          }
        })
      }, 60000)

      return () => clearInterval(intervalId)
    }
  }, [applications, refreshTime, startRefreshTimer]);

  if (applications && userProfile?.access_scope !== IAccessScope.system) {
    return (
        <div className={styles.myServices}>
          <section className={styles.section1}>
            <header>
              <h1><Translate id={'myServices.headline'} /></h1>
              <p><Translate id={'myServices.description'} /></p>
            </header>

            <CardContainer className={styles.cardContainer}>
              {applications.map((item, index) => {
                return (
                  item.multiple_service_account_linking
                              && !getItemFromLS(CERT_CENTRAL_TIP_DISMISSED) ?
                  <Tooltip
                    rootClassName={styles.customizeToolTip}
                    open={cardToolTip}
                    placement='top'
                    key={index}
                  
                    title={
                      <div className={styles.customToolTipContent}>
                        <Translate id='myServices.cardToolTip' data={{ name: item.name }} />
                        <button onClick={(e) => dismissToolTip(e)}><Icon icon={faTimes} color='white' size='lg' /></button>
                      </div>
                    }
                  >
                    {renderCard(item)}
                  </Tooltip>
                : <>{renderCard(item)}</>
              )
              })}
            </CardContainer>
          </section>

          {otherApplications && otherApplications?.length > 0 &&
              <section className={styles.exploreMoreServices}>
                <header>
                  <h1><Translate id={'myServices.otherServices'}/></h1>
                  <p><Translate id={'myServices.otherServicesDesc'}/></p>
                </header>
                <CardContainer className={styles.cardContainer}>
                  {otherApplications.map((item, index) => {
                    const desc = translate('applications.' + item.app_code.split('.')[0] + '.description')
                    return (
                        <Card
                            key={index}
                            size={CardSizeTypes.LARGE}
                            onClick={(e) => handleActionClick(e, item)}
                            header={
                              <div className={styles.customCardHeader}>
                                <div className={styles.iconContainer}>{renderManagerIcon(item.icon, item.name)}</div>
                                <p>{item.icon === 'stIcon' ? item.name + " | " + STMApplicationName.KEYLOCKER  : item.name}</p>
                              </div>
                            }
                            content={
                              <div className={styles.customCardContent}>
                                <p>
                                  {desc === 'description' ? item.description : desc}
                                </p>
                              </div>
                            }
                        />
                    )
                  })}
                </CardContainer>
              </section>
          }

          <StandardModal
              display={showServicesModal}
              buttonLabel={translate('common.form.buttons.okButton')}
              buttonCancelLabel={translate('common.form.buttons.cancelBtn')}
              headline={<CertCentralName/>}
              subHeading={translate('myServices.selectCertCentralAccount')}
              hideActions
              onCancelCallBack={() => setShowServicesModal(false)}>
            <>
              <p className={styles.modalDescription}>
                <Translate id='myServices.selectAccount' data={{ userName: userProfile?.user_name }}/>
              </p>

              <div className={styles.accountSelections}>
                {applications.map((item, index) => {
                  if (item.service_provider_accounts && item.service_provider_accounts.length > 1) {
                    return item.service_provider_accounts.map(acc => {
                      return (
                        <Card
                            className={styles.accountCards}
                            key={index}
                            size={CardSizeTypes.LARGE}
                            header={
                              <CardHeader
                                  label={
                                    <>
                                      <span className={styles.accountCardsLabel}>{acc.name}</span>
                                      <Icon className={styles.accountCardsIcon} icon={faExternalLinkSquare}></Icon>
                                    </>
                                  }
                                  description={acc.friendly_identifier}
                              />
                            }
                            onClick={() => handleAccountCardClick(acc.login_url)}
                        />
                    )
                    })
                  }
                })}
              </div>

              <div className={styles.accountSelectionActions}>
                <Button buttonType={ButtonType.SECONDARY} size='small' onClick={() => setShowServicesModal(false)}>{translate('common.form.buttons.cancelBtn')}</Button>
              </div>
            </>
          </StandardModal>
        </div>
    )
  }

  else return <></>
}

export default connect(
    (state: IAppState) => ({
      userProfile: state.userProfile.data,
      userApplications: state.userApplications.data
    }),
    { fetchUserProfile, getServices }
)(MyServices)